import React, { useState, useEffect, useRef } from 'react';
import { getAllQuestions, submitVote } from '../services/questionsService';
import CommentSection from './CommentSection';
import { useAuth } from '../context/AuthContext';
import QuestionImage from './QuestionImage';
import QuestionMedia from './QuestionMedia';
import imageCache from '../utils/imageCache';

function Categories({ hotMode = false, onCategoryClick }) {
  // Get current user from auth context
  const { currentUser } = useAuth();

  // Add state to track user interests
  const [interests, setInterests] = useState(() => {
    // Load saved interests from localStorage
    const savedInterests = localStorage.getItem('userInterests');
    return savedInterests ? JSON.parse(savedInterests) : {};
  });

  // States for handling votes and animation
  const [votedQuestions, setVotedQuestions] = useState({});
  const [animatingResults, setAnimatingResults] = useState(false);
  const [displayPercentA, setDisplayPercentA] = useState(0);
  const [displayPercentB, setDisplayPercentB] = useState(0);
  const [userChoices, setUserChoices] = useState({});
  const [animationStep, setAnimationStep] = useState(0); // 0: not started, 1: tallying, 2: complete
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Load voted questions from localStorage on component mount
  useEffect(() => {
    const loadVotedQuestions = () => {
      if (currentUser) {
        // User-specific storage key for logged-in users
        const userVotedQuestionsKey = `votedQuestions_${currentUser.uid}`;
        const votedQuestionsData = JSON.parse(localStorage.getItem(userVotedQuestionsKey) || '{}');
        setVotedQuestions(votedQuestionsData);

        // Load user choices for styling
        const userVoteOptionsKey = `userVoteOptions_${currentUser.uid}`;
        const userChoicesData = JSON.parse(localStorage.getItem(userVoteOptionsKey) || '{}');
        setUserChoices(userChoicesData);
      } else {
        // Anonymous-only storage
        const anonVotedQuestionsKey = `votedQuestions_anonymous`;
        const votedQuestionsData = JSON.parse(localStorage.getItem(anonVotedQuestionsKey) || '{}');
        setVotedQuestions(votedQuestionsData);

        // Load anonymous choices
        const anonVoteOptionsKey = `userVoteOptions_anonymous`;
        const userChoicesData = JSON.parse(localStorage.getItem(anonVoteOptionsKey) || '{}');
        setUserChoices(userChoicesData);
      }
    };

    loadVotedQuestions();
  }, [currentUser]);

  // State for notification message
  const [notification, setNotification] = useState({
    message: '',
    visible: false,
    category: null
  });

  // State to track which category is selected
  // Load from localStorage to persist across tab switches
  const [selectedCategoryId, setSelectedCategoryId] = useState(() => {
    const savedState = localStorage.getItem('categoriesViewState');
    return savedState ? JSON.parse(savedState).selectedCategoryId : null;
  });

  // State to hold category-specific questions
  const [categoryQuestions, setCategoryQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // State for managing question display/interactions
  // Load from localStorage to persist across tab switches
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const savedState = localStorage.getItem('categoriesViewState');
    return savedState ? JSON.parse(savedState).currentQuestionIndex : 0;
  });
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);

  // Refs for swipe detection
  const questionCardRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isScrollingRef = useRef(false);

  // Handle touch start event for swipe detection
  const handleTouchStart = (e) => {
    // Check if we're in the comments section
    if (showComments && e.target.closest('.comments-section')) {
      isScrollingRef.current = true;
      return;
    }

    isScrollingRef.current = false;
    touchStartX.current = e.touches[0].clientX;
  };

  // Handle touch end event for swipe detection
  const handleTouchEnd = (e) => {
    // If we started touch in comments area, ignore swipe
    if (isScrollingRef.current) return;

    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipe();
  };

  // Handle swipe logic
  const handleSwipe = () => {
    // Need at least 50px swipe distance to register as a swipe
    const minSwipeDistance = 50;
    const swipeDistance = touchEndX.current - touchStartX.current;

    if (Math.abs(swipeDistance) < minSwipeDistance) return;

    if (swipeDistance > 0) {
      // Swiped right - show previous question in category
      if (currentQuestionIndex > 0) {
        setCurrentQuestionIndex(prevIndex => prevIndex - 1);
      }
    } else {
      // Swiped left - show next question in category
      if (currentQuestionIndex < categoryQuestions.length - 1) {
        const nextIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIndex);
        
        // Preload next 3 images
        const imagesToPreload = categoryQuestions
          .slice(nextIndex + 1, nextIndex + 4)
          .map(q => q.imageUrl)
          .filter(Boolean);
        imageCache.preloadImages(imagesToPreload);
      }
    }
  };

  // Don't change body background - already set in CSS
  
  // Save state to localStorage whenever it changes
  useEffect(() => {
    const state = {
      selectedCategoryId,
      currentQuestionIndex,
      hotMode
    };
    localStorage.setItem('categoriesViewState', JSON.stringify(state));
  }, [selectedCategoryId, currentQuestionIndex, hotMode]);

  // Restore category questions on mount if we have a saved category
  useEffect(() => {
    const restoreCategory = async () => {
      if (selectedCategoryId && categoryQuestions.length === 0) {
        console.log('Restoring saved category:', selectedCategoryId);
        setLoadingQuestions(true);

        try {
          if (hotMode) {
            // For Hot tab, map category ID to algorithm name
            const hotAlgorithmMap = {
              1: 'trending',
              2: 'popular',
              3: 'rising',
              4: 'new'
            };
            const hotAlgorithm = hotAlgorithmMap[selectedCategoryId] || 'trending';
            const userId = currentUser ? currentUser.uid : 'anonymous_user';
            const allQuestions = await getAllQuestions(1, 50, userId, false, hotAlgorithm);
            setCategoryQuestions(allQuestions.questions);

            // Preload images
            const imagesToPreload = allQuestions.questions.slice(0, 5).map(q => q.imageUrl).filter(Boolean);
            imageCache.preloadImages(imagesToPreload);
          } else {
            // For regular categories
            const allQuestions = await getAllQuestions(1, 50);
            const filteredQuestions = allQuestions.questions.filter(
              question => question.category === selectedCategoryId.toString()
            );
            setCategoryQuestions(filteredQuestions);

            // Preload images
            const imagesToPreload = filteredQuestions.slice(0, 5).map(q => q.imageUrl).filter(Boolean);
            imageCache.preloadImages(imagesToPreload);
          }
        } catch (error) {
          console.error('Error restoring category:', error);
        } finally {
          setLoadingQuestions(false);
        }
      }
    };

    restoreCategory();
  }, []); // Only run on mount

  // Handle Categories tab clicks - DON'T reset, keep the state
  useEffect(() => {
    // Function to listen for category tab clicks
    const handleCategoriesTabClick = (e) => {
      // Keep state when switching back to categories tab
      console.log("Categories tab clicked - maintaining current view state");
      // Don't reset selectedCategoryId or categoryQuestions anymore
    };

    // Find the categories tab element using data-tab attribute
    const categoriesTab = document.querySelector('[data-tab="categories"]');
    if (categoriesTab) {
      categoriesTab.addEventListener('click', handleCategoriesTabClick);
    }

    // Cleanup - remove event listener
    return () => {
      if (categoriesTab) {
        categoriesTab.removeEventListener('click', handleCategoriesTabClick);
      }
    };
  }, [selectedCategoryId]); // Re-add event listener when selectedCategoryId changes

  // Toggle interest for a category
  const toggleInterest = (categoryId, categoryName) => {
    setInterests(prev => {
      const newValue = !prev[categoryId];
      const newInterests = {
        ...prev,
        [categoryId]: newValue
      };

      // Save to localStorage
      localStorage.setItem('userInterests', JSON.stringify(newInterests));

      // Show notification
      setNotification({
        message: newValue
          ? `Added ${categoryName} to your interests!`
          : `Removed ${categoryName} from your interests`,
        visible: true,
        category: categoryId
      });

      // Hide notification after 2 seconds
      setTimeout(() => {
        setNotification(prev => ({
          ...prev,
          visible: false
        }));
      }, 2000);

      return newInterests;
    });
  };

  // Helper function for easing animation
  const easeOutQuad = (t) => t * (2 - t);

  // Function to check if user has voted on a question - matches Home.js logic
  const hasUserVoted = (questionId) => {
    if (!questionId) return false;

    if (currentUser) {
      // User-specific storage key for logged-in users
      const userVotedQuestionsKey = `votedQuestions_${currentUser.uid}`;
      const votedQuestionsData = JSON.parse(localStorage.getItem(userVotedQuestionsKey) || '{}');
      const hasVoted = votedQuestionsData[questionId] !== undefined;
      console.log(`User ${currentUser.uid} vote check for question ${questionId}: ${hasVoted}`);
      return hasVoted;
    } else {
      // Anonymous-only storage - completely separate from logged-in users
      const anonVotedQuestionsKey = `votedQuestions_anonymous`;
      const votedQuestionsData = JSON.parse(localStorage.getItem(anonVotedQuestionsKey) || '{}');
      const hasVoted = votedQuestionsData[questionId] !== undefined;
      console.log(`Anonymous user vote check for question ${questionId}: ${hasVoted}`);
      return hasVoted;
    }
  };

  // Handle vote for a question
  const handleVote = async (questionId, option, question) => {
    if (hasUserVoted(questionId)) return;

    // If user is not logged in, show login modal
    if (!currentUser) {
      console.log("User not logged in, showing login modal");
      setLoginModalOpen(true);

      // Trigger the openLoginModal event to show the login modal
      const loginEvent = new CustomEvent('openLoginModal');
      window.dispatchEvent(loginEvent);
      // Prevent any vote processing for anonymous users
      return;
    }

    try {
      console.log(`Voting for option ${option} on question ${questionId}`);

      // Set vote in state immediately to update UI
      setVotedQuestions(prev => ({
        ...prev,
        [questionId]: option
      }));

      // Set user choice for styling
      setUserChoices(prev => ({
        ...prev,
        [questionId]: option
      }));

      // Set up animation
      setAnimatingResults(true);

      // Update question locally
      const updatedQuestion = { ...question };
      if (option === 'A') {
        updatedQuestion.optionAVotes += 1;
      } else {
        updatedQuestion.optionBVotes += 1;
      }
      updatedQuestion.totalVotes += 1;

      // Update questions array
      setCategoryQuestions(prevQuestions =>
        prevQuestions.map(q =>
          q.id === questionId ? updatedQuestion : q
        )
      );

      // Calculate vote percentages for animation
      const totalVotes = updatedQuestion.totalVotes;
      const newOptionAPercentage = totalVotes > 0 ? Math.round((updatedQuestion.optionAVotes / totalVotes) * 100) : 0;
      const newOptionBPercentage = totalVotes > 0 ? Math.round((updatedQuestion.optionBVotes / totalVotes) * 100) : 0;

      // Reset displayed percentages to 0 for the animation
      setDisplayPercentA(0);
      setDisplayPercentB(0);

      // Animate the percentage counters
      let step = 0;
      let animSteps = 25; // Number of animation steps

      const animationTimer = setInterval(() => {
        step++;
        const progressRatio = step / animSteps;

        // Calculate current animation values using easing
        setDisplayPercentA(Math.round(easeOutQuad(progressRatio) * newOptionAPercentage));
        setDisplayPercentB(Math.round(easeOutQuad(progressRatio) * newOptionBPercentage));

        // When animation completes
        if (step >= animSteps) {
          clearInterval(animationTimer);
          setDisplayPercentA(newOptionAPercentage);
          setDisplayPercentB(newOptionBPercentage);
          setAnimationStep(2); // Animation complete
          setAnimatingResults(false);
        }
      }, 40); // ~1 second total animation (40ms x 25 steps)

      // Submit the vote to the server
      await submitVote(questionId, option);

      // Store vote in localStorage
      if (currentUser) {
        // User-specific storage
        const userVotedQuestionsKey = `votedQuestions_${currentUser.uid}`;
        const userVoteOptionsKey = `userVoteOptions_${currentUser.uid}`;

        const votedQuestionsData = JSON.parse(localStorage.getItem(userVotedQuestionsKey) || '{}');
        votedQuestionsData[questionId] = option;
        localStorage.setItem(userVotedQuestionsKey, JSON.stringify(votedQuestionsData));

        const userChoicesData = JSON.parse(localStorage.getItem(userVoteOptionsKey) || '{}');
        userChoicesData[questionId] = option;
        localStorage.setItem(userVoteOptionsKey, JSON.stringify(userChoicesData));
      } else {
        // Anonymous storage
        const anonVotedQuestionsKey = `votedQuestions_anonymous`;
        const anonVoteOptionsKey = `userVoteOptions_anonymous`;

        const votedQuestionsData = JSON.parse(localStorage.getItem(anonVotedQuestionsKey) || '{}');
        votedQuestionsData[questionId] = option;
        localStorage.setItem(anonVotedQuestionsKey, JSON.stringify(votedQuestionsData));

        const userChoicesData = JSON.parse(localStorage.getItem(anonVoteOptionsKey) || '{}');
        userChoicesData[questionId] = option;
        localStorage.setItem(anonVoteOptionsKey, JSON.stringify(userChoicesData));
      }

    } catch (error) {
      console.error('Error submitting vote:', error);
    }
  };

  // Handle clicking on a category
  const handleCategoryClick = async (categoryId, categoryName) => {
    console.log(`Category clicked: ${categoryName} (ID: ${categoryId})`);
    setSelectedCategoryId(categoryId);

    // Fetch questions for this category
    setLoadingQuestions(true);
    try {
      // If in hotMode, use the category name as the hot algorithm
      if (hotMode) {
        // Map UI category to algorithm name (lowercase)
        const hotAlgorithm = categoryName.toLowerCase();
        console.log(`Hot mode activated with algorithm: ${hotAlgorithm}`);

        // Track user interest in this hot algorithm
        try {
          if (currentUser) {
            const hotTabPrefsKey = `hotTabPreferences_${currentUser.uid}`;
            const hotTabPrefs = JSON.parse(localStorage.getItem(hotTabPrefsKey) || '{}');

            // Increment the counter for this algorithm
            hotTabPrefs[hotAlgorithm] = (hotTabPrefs[hotAlgorithm] || 0) + 1;
            localStorage.setItem(hotTabPrefsKey, JSON.stringify(hotTabPrefs));
            console.log(`Updated hot tab preferences: ${hotAlgorithm} = ${hotTabPrefs[hotAlgorithm]}`);
          }
        } catch (err) {
          console.warn('Error updating hot tab preferences:', err);
        }

        // Get questions using the hot algorithm
        const userId = currentUser ? currentUser.uid : 'anonymous_user';
        const allQuestions = await getAllQuestions(1, 50, userId, false, hotAlgorithm);

        console.log(`Found ${allQuestions.questions.length} questions for hot category ${categoryName}`);
        setCategoryQuestions(allQuestions.questions);
        
        // Preload first 5 images
        const imagesToPreload = allQuestions.questions.slice(0, 5).map(q => q.imageUrl).filter(Boolean);
        imageCache.preloadImages(imagesToPreload);

        // Call the parent handler if provided
        if (onCategoryClick) {
          onCategoryClick(categoryId, allQuestions.questions, hotAlgorithm);
        }

        // Store the selected category in localStorage
        localStorage.setItem('selectedCategory', JSON.stringify({
          id: categoryId,
          name: categoryName,
          hotAlgorithm
        }));

        // Stay in the Categories tab but show the hot category's questions
        const categoryQuestionsEvent = new CustomEvent('showCategoryQuestions', {
          detail: {
            categoryId,
            categoryName,
            questions: allQuestions.questions,
            stayInCategoriesTab: true,
            hotAlgorithm
          }
        });
        window.dispatchEvent(categoryQuestionsEvent);
      }
      // Regular category mode
      else {
        const allQuestions = await getAllQuestions(1, 50); // Get a large batch of questions

        // Filter questions by category
        const filteredQuestions = allQuestions.questions.filter(
          question => question.category === categoryId.toString()
        );

        console.log(`Found ${filteredQuestions.length} questions for category ${categoryName}`);
        setCategoryQuestions(filteredQuestions);
        
        // Preload first 5 images
        const imagesToPreload = filteredQuestions.slice(0, 5).map(q => q.imageUrl).filter(Boolean);
        imageCache.preloadImages(imagesToPreload);

        // Call the parent handler if provided
        if (onCategoryClick) {
          onCategoryClick(categoryId, filteredQuestions);
        }

        // Store the selected category in localStorage for persistence
        localStorage.setItem('selectedCategory', JSON.stringify({
          id: categoryId,
          name: categoryName
        }));

        // Stay in the Categories tab but show the category's questions
        const categoryQuestionsEvent = new CustomEvent('showCategoryQuestions', {
          detail: {
            categoryId,
            categoryName,
            questions: filteredQuestions,
            stayInCategoriesTab: true // Flag to indicate we should stay in the Categories tab
          }
        });
        window.dispatchEvent(categoryQuestionsEvent);
      }
    } catch (error) {
      console.error('Error fetching questions for category/hot algorithm:', error);
    } finally {
      setLoadingQuestions(false);
    }
  };
  
  // Sample category data
  const categories = hotMode ? [
    { id: 1, name: 'Trending', color: '#FF6B00', emoji: '🔥' },
    { id: 2, name: 'Popular', color: '#E91E63', emoji: '👑' },
    { id: 3, name: 'Rising', color: '#2196F3', emoji: '📈' },
    { id: 4, name: 'New', color: '#4CAF50', emoji: '✨' }
  ] : [
    { id: 1, name: 'Life', color: '#FF6B00', emoji: '⚛️' },
    { id: 2, name: 'Food', color: '#E67E22', emoji: '🍔' },
    { id: 3, name: 'Dating', color: '#FFC107', emoji: '❤️' },
    { id: 4, name: 'Fashion', color: '#E91E63', emoji: '👗' },
    { id: 5, name: 'Politics', color: '#2196F3', emoji: '🗳️' },
    { id: 6, name: 'Sports', color: '#4CAF50', emoji: '⚽' },
    { id: 7, name: 'Technology', color: '#9C27B0', emoji: '📱' },
    { id: 8, name: 'Movies', color: '#FF5722', emoji: '🎬' },
    { id: 9, name: 'Music', color: '#607D8B', emoji: '🎵' },
    { id: 10, name: 'Out of the Box', color: '#3F51B5', emoji: '💭' }
  ];

  return (
    <div className="categories-container" style={{
      backgroundColor: '#111',
      minHeight: '100vh',
      padding: '0',
      margin: '0'
    }}>
      {/* Interest notification */}
      {notification.visible && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: interests[notification.category] ? 'rgba(76, 175, 80, 0.9)' : 'rgba(255, 152, 0, 0.9)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '50px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 1000,
            maxWidth: '90%',
            textAlign: 'center',
            fontSize: '16px',
            fontWeight: '500'
          }}
        >
          {notification.message}
        </div>
      )}

      {selectedCategoryId && categoryQuestions.length > 0 ? (
        /* Show category questions view matching the For You format */
        <div style={{
          padding: '0',
          backgroundColor: '#111',
          minHeight: '100vh'
        }}>
          {/* Back button to return to category list */}
          <button
            onClick={() => {
              setSelectedCategoryId(null);
              setCategoryQuestions([]);
              setCurrentQuestionIndex(0);
              localStorage.removeItem('categoriesViewState');
            }}
            style={{
              position: 'fixed',
              top: '80px',
              left: '15px',
              zIndex: 100,
              background: 'rgba(0, 0, 0, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '50%',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '20px',
              color: 'white',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.2s ease',
              WebkitTapHighlightColor: 'transparent'
            }}
            aria-label="Back to categories"
          >
            ←
          </button>

          {/* Show current category question */}
          {categoryQuestions.length > 0 && (
            <div key={categoryQuestions[currentQuestionIndex].id} style={{ backgroundColor: '#111', paddingTop: '0' }}>
              {/* Question Card */}
              <section
                className="question-card"
                ref={questionCardRef}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <div className="question-image-container">
                  <QuestionMedia
                    src={categoryQuestions[currentQuestionIndex].imageUrl}
                    alt={categoryQuestions[currentQuestionIndex].questionText}
                    className="question-image"
                    preloadNext={currentQuestionIndex < categoryQuestions.length - 1 ? categoryQuestions[currentQuestionIndex + 1].imageUrl : null}
                    mediaType={categoryQuestions[currentQuestionIndex].mediaType || 'image'}
                    thumbnailUrl={categoryQuestions[currentQuestionIndex].thumbnailUrl}
                  />
                  <div className="overlay"></div>
                </div>

                <div className="question-content">
                  <h2 className="question-text">
                    {categoryQuestions[currentQuestionIndex].questionText}
                  </h2>

                  {/* Standard poll options - show vote buttons if not voted */}
                  {categoryQuestions[currentQuestionIndex].questionType !== 'leaderboard' && !hasUserVoted(categoryQuestions[currentQuestionIndex].id) && (
                    <div className="vote-options">
                      <button
                        className="vote-button"
                        onClick={() => handleVote(categoryQuestions[currentQuestionIndex].id, 'A', categoryQuestions[currentQuestionIndex])}
                        style={{
                          backgroundColor: categoryQuestions[currentQuestionIndex].styles?.optionABackgroundColor || 'white',
                          color: categoryQuestions[currentQuestionIndex].styles?.optionATextColor || 'black',
                          border: categoryQuestions[currentQuestionIndex].styles?.optionABackgroundColor === 'white' ? 'none' : `2px solid ${categoryQuestions[currentQuestionIndex].styles?.optionATextColor || 'black'}`,
                          padding: '12px 20px',
                          borderRadius: '30px',
                          fontSize: '16px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        {categoryQuestions[currentQuestionIndex].optionA}
                      </button>
                      <button
                        className="vote-button"
                        onClick={() => handleVote(categoryQuestions[currentQuestionIndex].id, 'B', categoryQuestions[currentQuestionIndex])}
                        style={{
                          backgroundColor: categoryQuestions[currentQuestionIndex].styles?.optionBBackgroundColor || 'black',
                          color: categoryQuestions[currentQuestionIndex].styles?.optionBTextColor || 'white',
                          border: categoryQuestions[currentQuestionIndex].styles?.optionBBackgroundColor === 'black' ? '2px solid white' : `2px solid ${categoryQuestions[currentQuestionIndex].styles?.optionBTextColor || 'white'}`,
                          padding: '12px 20px',
                          borderRadius: '30px',
                          fontSize: '16px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        {categoryQuestions[currentQuestionIndex].optionB}
                      </button>
                    </div>
                  )}

                  {/* Vote results for standard questions after voting */}
                  {categoryQuestions[currentQuestionIndex].questionType !== 'leaderboard' && hasUserVoted(categoryQuestions[currentQuestionIndex].id) && currentUser && (
                    <div className={`vote-results ${categoryQuestions[currentQuestionIndex].id in votedQuestions && animatingResults ? 'animating' : ''}`}>
                      <div className="results-bar-container">
                        <div className="results-bar">
                          <div className="results-bar-divider" style={{ left: `${animatingResults ? displayPercentA : (categoryQuestions[currentQuestionIndex].totalVotes ? Math.round((categoryQuestions[currentQuestionIndex].optionAVotes / categoryQuestions[currentQuestionIndex].totalVotes) * 100) : 0)}%` }}></div>
                          {/* Option A bar */}
                          <div
                            className={`option-a-bar ${userChoices[categoryQuestions[currentQuestionIndex].id] === 'A' ? 'user-choice' : ''}
                              ${categoryQuestions[currentQuestionIndex].optionAVotes >= categoryQuestions[currentQuestionIndex].optionBVotes ? 'winner' : ''}`}
                            style={{
                              width: `${categoryQuestions[currentQuestionIndex].id in votedQuestions && animatingResults ?
                                displayPercentA :
                                (categoryQuestions[currentQuestionIndex].totalVotes ? Math.round((categoryQuestions[currentQuestionIndex].optionAVotes / categoryQuestions[currentQuestionIndex].totalVotes) * 100) : 0)}%`,
                              backgroundColor: categoryQuestions[currentQuestionIndex].styles?.optionABackgroundColor || 'white',
                              minWidth: categoryQuestions[currentQuestionIndex].totalVotes > 0 ? '1%' : '0%'
                            }}
                          >
                            {(animatingResults ? displayPercentA :
                              (categoryQuestions[currentQuestionIndex].totalVotes ?
                                Math.round((categoryQuestions[currentQuestionIndex].optionAVotes / categoryQuestions[currentQuestionIndex].totalVotes) * 100) : 0)) >= 15 && (
                              <span className="bar-text">{animatingResults ? displayPercentA :
                              (categoryQuestions[currentQuestionIndex].totalVotes ?
                                Math.round((categoryQuestions[currentQuestionIndex].optionAVotes / categoryQuestions[currentQuestionIndex].totalVotes) * 100) : 0)}%</span>
                            )}
                          </div>

                          {/* Option B bar */}
                          <div
                            className={`option-b-bar ${userChoices[categoryQuestions[currentQuestionIndex].id] === 'B' ? 'user-choice' : ''}
                              ${categoryQuestions[currentQuestionIndex].optionBVotes >= categoryQuestions[currentQuestionIndex].optionAVotes ? 'winner' : ''}`}
                            style={{
                              width: `${categoryQuestions[currentQuestionIndex].id in votedQuestions && animatingResults ?
                                displayPercentB :
                                (categoryQuestions[currentQuestionIndex].totalVotes ? Math.round((categoryQuestions[currentQuestionIndex].optionBVotes / categoryQuestions[currentQuestionIndex].totalVotes) * 100) : 0)}%`,
                              backgroundColor: categoryQuestions[currentQuestionIndex].styles?.optionBBackgroundColor || 'black',
                              minWidth: categoryQuestions[currentQuestionIndex].totalVotes > 0 ? '1%' : '0%'
                            }}
                          >
                            {(animatingResults ? displayPercentB :
                              (categoryQuestions[currentQuestionIndex].totalVotes ?
                                Math.round((categoryQuestions[currentQuestionIndex].optionBVotes / categoryQuestions[currentQuestionIndex].totalVotes) * 100) : 0)) >= 15 && (
                              <span className="bar-text">{animatingResults ? displayPercentB :
                              (categoryQuestions[currentQuestionIndex].totalVotes ?
                                Math.round((categoryQuestions[currentQuestionIndex].optionBVotes / categoryQuestions[currentQuestionIndex].totalVotes) * 100) : 0)}%</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Results percentages */}
                      <div className="results-percentage">
                        <div className={`percentage-container ${userChoices[categoryQuestions[currentQuestionIndex].id] === 'A' ? 'user-choice' : ''}
                          ${categoryQuestions[currentQuestionIndex].optionAVotes >= categoryQuestions[currentQuestionIndex].optionBVotes ? 'winner' : ''}`}>
                          <div className="percentage-value" style={{
                            color: categoryQuestions[currentQuestionIndex].styles?.optionABackgroundColor || 'white'
                          }}>
                            {categoryQuestions[currentQuestionIndex].id in votedQuestions && animatingResults ?
                              displayPercentA :
                              (categoryQuestions[currentQuestionIndex].totalVotes ? Math.round((categoryQuestions[currentQuestionIndex].optionAVotes / categoryQuestions[currentQuestionIndex].totalVotes) * 100) : 0)}%
                          </div>
                          <div className="percentage-item" style={{
                            color: categoryQuestions[currentQuestionIndex].styles?.optionABackgroundColor || 'white',
                            fontWeight: categoryQuestions[currentQuestionIndex].optionAVotes >= categoryQuestions[currentQuestionIndex].optionBVotes ? '800' : '500'
                          }}>
                            {categoryQuestions[currentQuestionIndex].optionA}
                          </div>
                        </div>
                        <div className={`percentage-container ${userChoices[categoryQuestions[currentQuestionIndex].id] === 'B' ? 'user-choice' : ''}
                          ${categoryQuestions[currentQuestionIndex].optionBVotes >= categoryQuestions[currentQuestionIndex].optionAVotes ? 'winner' : ''}`}>
                          <div className="percentage-value" style={{
                            color: categoryQuestions[currentQuestionIndex].styles?.optionBBackgroundColor || 'black'
                          }}>
                            {categoryQuestions[currentQuestionIndex].id in votedQuestions && animatingResults ?
                              displayPercentB :
                              (categoryQuestions[currentQuestionIndex].totalVotes ? Math.round((categoryQuestions[currentQuestionIndex].optionBVotes / categoryQuestions[currentQuestionIndex].totalVotes) * 100) : 0)}%
                          </div>
                          <div className="percentage-item" style={{
                            color: categoryQuestions[currentQuestionIndex].styles?.optionBBackgroundColor || 'black',
                            fontWeight: categoryQuestions[currentQuestionIndex].optionBVotes >= categoryQuestions[currentQuestionIndex].optionAVotes ? '800' : '500'
                          }}>
                            {categoryQuestions[currentQuestionIndex].optionB}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Login prompt for anonymous users who tried to vote */}
                  {categoryQuestions[currentQuestionIndex].questionType !== 'leaderboard' && hasUserVoted(categoryQuestions[currentQuestionIndex].id) && !currentUser && (
                    <div style={{
                      textAlign: 'center',
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      padding: '15px',
                      borderRadius: '10px',
                      margin: '15px 0'
                    }}>
                      <p style={{
                        color: 'white',
                        marginBottom: '10px',
                        fontSize: '16px'
                      }}>Sign in to view voting results</p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const loginEvent = new CustomEvent('openLoginModal');
                          window.dispatchEvent(loginEvent);
                        }}
                        style={{
                          backgroundColor: '#FF6B00',
                          color: 'white',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '20px',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        Sign In
                      </button>
                    </div>
                  )}

                  {/* For leaderboard questions */}
                  {categoryQuestions[currentQuestionIndex].questionType === 'leaderboard' && (
                    <div style={{
                      padding: '15px',
                      backgroundColor: 'rgba(0,0,0,0.3)',
                      borderRadius: '10px',
                      marginBottom: '15px'
                    }}>
                      <h3 style={{ fontSize: '16px', marginBottom: '8px' }}>Comment Leaderboard Question</h3>
                      <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
                        This question shows a leaderboard of top takes.
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* Takes Section */}
              <section className="comments-section"
              >
                <h3 className="comments-header"
                >
                  Takes
                </h3>
                <div className="comments-scroll-area">
                  <CommentSection
                    key={categoryQuestions[currentQuestionIndex].id}
                    questionId={categoryQuestions[currentQuestionIndex].id}
                    onCommentCountChange={(count) => setCommentCount(count)}
                    isLeaderboard={categoryQuestions[currentQuestionIndex].questionType === 'leaderboard'}
                  />
                </div>
              </section>
            </div>
          )}
        </div>
      ) : (
        /* Show categories grid view */
        <>
          <h2 style={{
            textAlign: "center",
            marginBottom: "15px",
            fontSize: "24px",
            color: "white",
            marginTop: "80px" /* Position below navigation bar */
          }}>
            {hotMode ? "Hot Topics" : "Browse Categories"}
          </h2>

          <p style={{
            textAlign: "center",
            marginBottom: "15px",
            fontSize: "14px",
            color: "rgba(255,255,255,0.7)",
            padding: "0 20px"
          }}>
            Click the ☆ to star categories you're interested in for your personalized feed
          </p>


          <div className="categories-grid">
            {categories.map(category => (
              <div
                className="category-box"
                key={category.id}
                style={{
                  backgroundColor: category.color,
                  border: selectedCategoryId === category.id ? '3px solid white' : 'none',
                  boxShadow: selectedCategoryId === category.id ? '0 0 15px rgba(255,255,255,0.5)' : 'none'
                }}
                onClick={() => handleCategoryClick(category.id, category.name)}
              >
                {/* Star button for interests with tooltip */}
                <div
                  className={`category-star ${interests[category.id] ? 'starred' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering the parent click
                    toggleInterest(category.id, category.name);
                  }}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    color: interests[category.id] ? '#FFD700' : 'rgba(255,255,255,0.7)',
                    fontSize: '22px',
                    cursor: 'pointer',
                    zIndex: 10,
                    textShadow: interests[category.id] ? '0 0 5px rgba(255,215,0,0.7)' : 'none',
                    transition: 'all 0.3s ease',
                    transform: interests[category.id] ? 'scale(1.2)' : 'scale(1)'
                  }}
                  title={interests[category.id] ?
                         `${category.name} added to your interests` :
                         `Click to add ${category.name} to your interests`}
                >
                  {interests[category.id] ? '★' : '☆'}
                </div>
                <div className="category-emoji">{category.emoji}</div>
                <div className="category-name">{category.name}</div>

                {/* Selection indicator */}
                {selectedCategoryId === category.id && (
                  <div style={{
                    position: 'absolute',
                    bottom: '5px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'white',
                    color: category.color,
                    fontSize: '12px',
                    padding: '3px 10px',
                    borderRadius: '12px',
                    fontWeight: 'bold'
                  }}>
                    Selected
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default Categories;